import { useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CheckIcon,
  ClockIcon,
  LayersIcon,
  PencilIcon,
  TagIcon,
  TrashIcon,
} from '../../../ui/icons';
import { conflictAnchor, focusGroup, groupAnchor } from '../blockDom';
import { useDayData } from '../DayDataContext';
import { PickerFocus, PickerKind, useDayUi } from '../dayUiStore';
import { useDayCommands } from '../useDayCommands';
import { BlockContextMenu, type ContextMenuEntry } from './BlockContextMenu';
import { ContextMenuItem, contextMenuSections } from './contextMenuModel';

const ICONS: Record<ContextMenuItem, ReactNode> = {
  [ContextMenuItem.EditText]: <PencilIcon />,
  [ContextMenuItem.ChangeProject]: <TagIcon />,
  [ContextMenuItem.EditTime]: <ClockIcon />,
  [ContextMenuItem.Confirm]: <CheckIcon />,
  [ContextMenuItem.Overlay]: <LayersIcon />,
  [ContextMenuItem.ViewOverlap]: <LayersIcon />,
  [ContextMenuItem.Delete]: <TrashIcon />,
};

/**
 * Renders the block context menu for the open `contextMenu` state of the day it belongs to
 * (2026-10b §6) and runs its items through the same commands and picker entry points the
 * picker's own rows and the keyboard use. Choosing an item closes the menu first, hands the
 * focus back to the block (so the picker it may open returns focus there on Esc), then acts.
 */
export function BlockContextMenuHost() {
  const { t } = useTranslation();
  const data = useDayData();
  const commands = useDayCommands();
  const menu = useDayUi((state) => state.contextMenu);
  const closeContextMenu = useDayUi((state) => state.closeContextMenu);

  const active = menu !== null && menu.date === data.date;
  const group = active ? data.groups.find((candidate) => candidate.key === menu.groupKey) : undefined;
  // The block vanished under the open menu (sync, undo from elsewhere): the menu goes with it.
  const gone = active && group === undefined && !data.blocksLoading;
  useEffect(() => {
    if (gone) closeContextMenu();
  }, [gone, closeContextMenu]);

  if (menu === null || !active || group === undefined) return null;

  const overlapping = data.conflicts.pairs.some(([a, b]) => a === group.key || b === group.key);
  const labels: Record<ContextMenuItem, string> = {
    [ContextMenuItem.EditText]: t('contextMenu.editText'),
    [ContextMenuItem.ChangeProject]: t('contextMenu.changeProject'),
    [ContextMenuItem.EditTime]: t('contextMenu.editTime'),
    [ContextMenuItem.Confirm]: t('contextMenu.confirm'),
    [ContextMenuItem.Overlay]: t('contextMenu.overlay'),
    [ContextMenuItem.ViewOverlap]: t('contextMenu.viewOverlap'),
    [ContextMenuItem.Delete]:
      group.members.length > 1
        ? t('contextMenu.deleteMany', { count: group.memberIds.length })
        : t('contextMenu.delete'),
  };
  const sections: ContextMenuEntry[][] = contextMenuSections({
    status: group.status,
    memberCount: group.members.length,
    overlapping,
  }).map((section) => section.map((id) => ({ id, label: labels[id], icon: ICONS[id] })));

  const run = (item: ContextMenuItem): void => {
    const ui = useDayUi.getState();
    const anchor = groupAnchor(group.key) ?? {
      left: menu.x,
      top: menu.y,
      right: menu.x,
      bottom: menu.y,
    };
    ui.closeContextMenu();
    focusGroup(group.key);
    const openEdit = (focus: PickerFocus): void =>
      ui.openPicker({ kind: PickerKind.Edit, date: data.date, groupKey: group.key, anchor, focus });
    switch (item) {
      case ContextMenuItem.EditText:
        openEdit(PickerFocus.Title);
        return;
      case ContextMenuItem.ChangeProject:
        openEdit(PickerFocus.List);
        return;
      case ContextMenuItem.EditTime:
        openEdit(PickerFocus.Start);
        return;
      case ContextMenuItem.Confirm:
        void commands.confirmGroup(group);
        return;
      case ContextMenuItem.Overlay:
        ui.openPicker({
          kind: PickerKind.Create,
          date: data.date,
          span: { startMin: group.startMin, endMin: group.endMin },
          anchor,
        });
        return;
      case ContextMenuItem.ViewOverlap:
        ui.openConflict({ date: data.date, id: group.key, anchor: conflictAnchor(group.key) ?? anchor });
        return;
      case ContextMenuItem.Delete:
        ui.select(null);
        void commands.clearGroup(group);
        return;
    }
  };

  return (
    <BlockContextMenu
      label={t('contextMenu.label')}
      sections={sections}
      x={menu.x}
      y={menu.y}
      onSelect={run}
      onClose={(restoreFocus) => {
        closeContextMenu();
        if (restoreFocus) focusGroup(group.key);
      }}
    />
  );
}
